import { type ComponentProps, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { send } from 'loot-core/platform/client/fetch';
import {
  addNotification,
  createCategory,
  deleteCategory,
  pushModal,
  updateCategory,
} from 'loot-core/src/client/actions';
import { type CategoryEntity } from 'loot-core/types/models';

import { SvgCheveronDown, SvgEditPencil } from '../../icons/v1';
import { SvgViewHide, SvgViewShow } from '../../icons/v2';
import { SvgDragIcon } from '../../icons/v2/DragIcon';
import { SvgOffBudget } from '../../icons/v2/SvgOffBudget';
import { SvgOnBudget } from '../../icons/v2/SvgOnBudget';
import { SvgTrash } from '../../icons/v2/Trash';
import { useResponsive } from '../../ResponsiveProvider';
import { theme } from '../../style';
import { Button } from '../common/Button';
import { Menu } from '../common/Menu';
import { Popover } from '../common/Popover';
import { View } from '../common/View';
import {
  type DragState,
  DropHighlight,
  type OnDragChangeCallback,
  type OnDropCallback,
  useDraggable,
  useDroppable,
} from '../sort';
import { InputCell } from '../table';

import type ManageCategoriesCategoryGroup from './ManageCategoriesCategoryGroup';

type ManageCategoriesCategoryItemProps = {
  category: CategoryEntity;
  isCategoryGroupHidden: boolean;
  indent?: boolean;
  onHideNewCategory: () => void;
  dragState?: DragState<
    ComponentProps<typeof ManageCategoriesCategoryGroup>['categoryGroup']
  >;
  onDragChange?: OnDragChangeCallback<
    ComponentProps<typeof ManageCategoriesCategoryGroup>['categoryGroup']
  >;
  onReorderCategory?: OnDropCallback;
};

const ManageCategoriesCategoryItem: React.FC<
  ManageCategoriesCategoryItemProps
> = ({
  category,
  indent,
  isCategoryGroupHidden,
  onHideNewCategory,
  dragState,
  onDragChange,
  onReorderCategory,
}) => {
  const temporary = category.id === 'new';
  const triggerRef = useRef(null);
  const dispatch = useDispatch();
  const { width, isNarrowWidth } = useResponsive();

  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  let dragging = dragState && dragState.item === category;

  if (dragState && dragState.item.id === category.cat_group) {
    dragging = true;
  }

  const { dragRef } = useDraggable({
    type: category.is_income ? 'income-category' : 'expense-category',
    onDragChange,
    item: category,
    canDrag: !editing && !isNarrowWidth,
  });

  const { dropRef, dropPos } = useDroppable({
    types: category.is_income ? 'income-category' : 'expense-category',
    id: category.id,
    onDrop: onReorderCategory,
    canDrop: () => !isNarrowWidth,
  });

  const onEditName = isEditing => {
    setEditing(isEditing);
  };

  const onDeleteCategory = async id => {
    const mustTransfer = await send('must-category-transfer', { id });

    if (mustTransfer) {
      dispatch(
        pushModal('confirm-category-delete', {
          category: id,
          onDelete: transferCategory => {
            if (id !== transferCategory) {
              dispatch(deleteCategory(id, transferCategory));
            }
          },
        }),
      );
    } else {
      dispatch(deleteCategory(id));
    }
  };

  const categoryNameAlreadyExistsNotification = name => {
    dispatch(
      addNotification({
        type: 'error',
        message: `Category ‘${name}’ already exists in group (May be Hidden)`,
      }),
    );
  };

  const onSaveCategory = async category => {
    const cats = await send('get-categories');
    const exists =
      cats.grouped
        .filter(g => g.id === category.cat_group)[0]
        .categories.filter(
          c => c.name.toUpperCase() === category.name.toUpperCase(),
        )
        .filter(c => (category.id === 'new' ? true : c.id !== category.id))
        .length > 0;

    if (exists) {
      categoryNameAlreadyExistsNotification(category.name);
      return;
    }

    if (category.id === 'new') {
      dispatch(
        createCategory(
          category.name,
          category.cat_group,
          category.is_income,
          category.hidden,
        ),
      );
    } else {
      dispatch(updateCategory(category));
    }
    if (category.id === 'new') {
      onHideNewCategory();
    }
  };

  const getCategoryActionsView = () => {
    return width <= 850 ? (
      <View style={{ flexShrink: 0 }} ref={triggerRef}>
        <Button
          type="bare"
          className="hover-visible"
          onClick={e => {
            e.stopPropagation();
            setMenuOpen(true);
          }}
          style={{ color: 'currentColor', padding: 3 }}
        >
          <SvgCheveronDown
            width={14}
            height={14}
            style={{ color: 'currentColor' }}
          />
        </Button>
        <Popover
          triggerRef={triggerRef}
          placement="bottom start"
          isOpen={menuOpen}
          onOpenChange={() => setMenuOpen(false)}
          style={{ width: 200 }}
        >
          <Menu
            onMenuSelect={type => {
              if (type === 'rename') {
                onEditName(true);
              } else if (type === 'delete') {
                onDeleteCategory(category.id);
              } else if (type === 'toggle-visibility') {
                onSaveCategory({ ...category, hidden: !category.hidden });
              } else if (type === 'set-on-off-budget') {
                onSaveCategory({
                  ...category,
                  is_offbudget: !category.is_offbudget,
                });
              }
              setMenuOpen(false);
            }}
            items={[
              {
                name: 'set-on-off-budget',
                text: category.is_offbudget
                  ? 'Set as For-budget'
                  : 'Set as Off-budget',
              },
              !isCategoryGroupHidden && {
                name: 'toggle-visibility',
                text: category.hidden ? 'Show' : 'Hide',
              },
              { name: 'rename', text: 'Rename' },
              { name: 'delete', text: 'Delete' },
            ]}
          />
        </Popover>
      </View>
    ) : (
      <View
        style={{
          flexShrink: 0,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Button
          type="bare"
          onClick={e => {
            e.stopPropagation();
            onSaveCategory({
              ...category,
              is_offbudget: !category.is_offbudget,
            });
          }}
          style={{
            padding: 5,
            flexDirection: 'row',
            gap: 4,
            alignItems: 'center',
            fontSize: 12,
          }}
        >
          {category.is_offbudget ? (
            <SvgOnBudget
              width={17}
              height={17}
              style={{ fill: 'currentColor' }}
            />
          ) : (
            <SvgOffBudget
              width={17}
              height={17}
              style={{ fill: 'currentColor' }}
            />
          )}
          {category.is_offbudget ? 'Set as For-budget' : 'Set as Off-budget'}
        </Button>
        <Button
          type="bare"
          onClick={e => {
            e.stopPropagation();
            onSaveCategory({ ...category, hidden: !category.hidden });
          }}
          style={{
            padding: 5,
            flexDirection: 'row',
            gap: 4,
            alignItems: 'center',
            fontSize: 12,
          }}
        >
          {category.hidden ? (
            <SvgViewShow width={14} height={14} />
          ) : (
            <SvgViewHide width={14} height={14} />
          )}
          {category.hidden ? 'Show' : 'Hide'}
        </Button>
        <Button
          type="bare"
          onClick={e => {
            e.stopPropagation();
            onEditName(category.id);
          }}
          style={{
            padding: 5,
            flexDirection: 'row',
            gap: 4,
            alignItems: 'center',
            fontSize: 12,
          }}
        >
          <SvgEditPencil width={11} height={11} />
          Rename
        </Button>
        <Button
          type="bare"
          onClick={e => {
            e.stopPropagation();
            onDeleteCategory(category.id);
          }}
          style={{
            padding: 5,
            flexDirection: 'row',
            gap: 4,
            alignItems: 'center',
            fontSize: 12,
          }}
        >
          <SvgTrash width={16} height={16} style={{ strokeWidth: 2 }} />
          Delete
        </Button>
      </View>
    );
  };

  const displayed = (
    <View
      innerRef={dragRef}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        gap: 8,
        opacity: category.hidden || isCategoryGroupHidden ? 0.3 : 1,
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flex: 1,
        }}
      >
        {!isNarrowWidth && (
          <SvgDragIcon
            width={13}
            height={13}
            style={{ color: theme.pageTextSubdued, cursor: 'grab' }}
          />
        )}
        {category.name}
      </span>
      {getCategoryActionsView()}
    </View>
  );

  return (
    <View
      innerRef={dropRef}
      key={category.id}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        height: 40,
        padding: '0 24px',
        gap: 8,
        backgroundColor: theme.tableBackground,
        ...(indent ? { paddingLeft: 8 + 24 } : {}),
      }}
    >
      <DropHighlight pos={dropPos} offset={{ top: 1 }} />
      <View
        style={{ flex: 1, flexDirection: 'row', width: '100%', height: '100%' }}
      >
        <View
          data-adoption-id="manage-categories-category-item"
          onKeyDown={e => {
            if (e.key === 'Enter') {
              onEditName(false);
              e.stopPropagation();
            }
          }}
          style={{
            flex: 1,
            height: '100%',
            ...(!dragging &&
              !dragging &&
              dragState?.preview && {
                '&:hover .hover-visible': {
                  display: 'flex',
                },
              }),
            ...(dragging && { color: theme.formInputTextPlaceholderSelected }),
            // The zIndex here forces the the view on top of a row below
            // it that may be "collapsed" and show a border on top
            ...(dragging &&
              dragState.preview && {
                backgroundColor: theme.tableBackground,
                zIndex: 10000,
                borderRadius: 6,
                overflow: 'hidden',
              }),
          }}
        >
          <InputCell
            value={category.name}
            data-adoption-id="category-name"
            formatter={() => displayed}
            linkStyle={{ height: '100%', paddingLeft: 0 }}
            valueStyle={{ padding: 0 }}
            width="flex"
            exposed={editing || temporary}
            onUpdate={value => {
              const newValue = value.trim();
              if (temporary) {
                if (newValue === '') {
                  onHideNewCategory();
                } else if (newValue !== '') {
                  onSaveCategory({ ...category, name: newValue });
                }
              } else {
                if (newValue && newValue !== category.name) {
                  onSaveCategory({ ...category, name: value });
                }
              }
            }}
            onBlur={() => onEditName(false)}
            style={{ border: 'none' }}
            inputProps={{
              placeholder: temporary ? 'New Category Name' : '',
            }}
          />
        </View>
      </View>
    </View>
  );
};
export default ManageCategoriesCategoryItem;
